/**
 * ocrCrossCheck — still OCR(카드번호·카드명)로 매칭 후보를 교차 검증한다.
 *
 * 배경: 인덱스에는 같은 일러스트의 언어판·세트 차이 카드가 나란히 있어 top-1/top-2 가
 * 유사도 접전인 경우가 흔하다. 컬렉터 번호("025/165", "TG12/TG30" 등)는 그 축을
 * 가르는 독립 신호다 — OCR 번호와 카탈로그 CARD_NO 가 일치하는 후보를 우대한다.
 * 카드명(언어별 OCR, 2026-08-14)은 **번호와의 결합으로만** 쓴다 — 이름+번호 동시
 * 일치는 독립 신호 2개가 겹친 것이라 유사도 격차 제한 없이 top-1 로 승격한다.
 *
 * ⚠ 이름 단독 신호를 쓰지 않는 이유: 같은 이름의 인쇄판이 수십 장이라 이름은 인쇄판을
 * 못 가르고, 진화 카드에는 "○○에서 진화"로 **남의 카드명**이 인쇄돼 있어 이름 단독
 * 일치는 오히려 오답(진화 전 카드)을 확인해줄 수 있다.
 *
 * 보수 원칙:
 *  - 번호 단독 재정렬은 "top-1 은 번호 불일치, 하위 후보는 일치, 유사도 차이가 근소" 일 때만.
 *  - matched(확신) 플래그는 건드리지 않는다 — 엔진 sim_accept/sim_margin 이 권위.
 *  - OCR 실패·번호 미검출·카탈로그 미보유는 전부 no-op (검증 없이 원본 유지).
 */
export interface CrossCheckEntry {
  cardSerlno: number;
  similarity: number;
}

/**
 * 교차검증이 실제로 읽는 필드만 추린 형태 — 카탈로그 조회 결과(`CardItem`)와 사이드카
 * (`card_meta.bin`) 둘 다 이 모양을 만족한다. 좁혀 둔 이유는 **출처를 바꿔 끼우려는
 * 것**이다: 사이드카가 실린 릴리스에서는 서버 왕복 없이 같은 판정을 한다.
 */
export interface CrossCheckCard {
  name: string;
  number: string | null;
  cardsetCode?: string | null;
  /** 기술명(카드 언어 그대로) — 접전 재랭킹 보조축. 사이드카에는 없어 선택 필드 */
  attackNames?: string[];
}

/**
 * 엔진 매치 결과에 실려 온 사이드카 값 → 교차검증용 맵.
 *
 * 사이드카가 없는 릴리스에서는 필드가 전부 undefined 라 빈 맵이 나오고, 호출부는
 * 종전대로 카탈로그 API 로 메운다. **부분적으로 채워진 맵을 만들지 않는다** — 이름이
 * 없는 행을 "이름 없음"으로 넘기면 이름 증거 게이트가 정상 카드를 거부한다.
 */
export function crossCheckCardsFrom(
  matches: Array<CrossCheckEntry & { name?: string; number?: string; setCode?: string }>,
): Map<number, CrossCheckCard> {
  const out = new Map<number, CrossCheckCard>();
  for (const m of matches) {
    if (m.name === undefined) continue;
    out.set(m.cardSerlno, {
      name: m.name,
      number: m.number ?? null,
      cardsetCode: m.setCode ?? null,
    });
  }
  return out;
}

export interface CrossCheckResult<T extends CrossCheckEntry> {
  /** 재정렬 반영된 후보 목록 (재정렬 없으면 입력 그대로) */
  matches: T[];
  /** OCR 번호와 카탈로그 번호가 일치한 후보 — UI "번호 일치" 표시용 */
  confirmedSerlnos: Set<number>;
  /** OCR 텍스트에 카탈로그 카드명이 나타난 후보 — 번호 일치와의 교집합만 의미 있다 */
  nameConfirmedSerlnos: Set<number>;
  /** OCR 텍스트에 세트 코드가 나타난 후보 — 번호가 안 읽힐 때의 재판 판별 축 */
  setConfirmedSerlnos: Set<number>;
  /** top-1 이 바뀌었는가 */
  reordered: boolean;
}

/**
 * 재정렬 허용 유사도 차이 상한 — 이보다 벌어져 있으면 임베딩 판단을 존중한다.
 * 분모까지 일치한 전체형 번호가 top-5 안에서 우연히 맞을 확률은 낮아 폭을 넉넉히
 * 두지만, 무제한은 아니다 — OCR 오독(0↔8 등)이 이웃 번호 후보를 "확인"해줄 수 있다.
 */
export const CROSS_CHECK_MAX_SIM_GAP = 0.12;

/** 숫자 런의 선행 0 제거 — "025/165" 와 "25/165" 를 같은 번호로 본다 */
function canonical(value: string): string {
  return value
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/\d+/g, (digits) => String(Number(digits)));
}

/**
 * 카탈로그 CARD_NO 정규화 — **첫 공백 전 토큰만**. 수집서버가 한국어판 번호에
 * 레어도를 붙여 보낸다("002/081 C", 2026-08-14 실 DB 확인). 그대로 쓰면 canonical 이
 * "2/81C" 가 돼 OCR 대조가 ko 카드에서 전부 불일치한다. 표시도 이 값을 쓴다
 * (레어도는 별도 필드로 이미 나온다).
 */
export function catalogCardNumber(cardNumber: string): string {
  return cardNumber.trim().split(/\s+/)[0];
}

/**
 * OCR 이 숫자를 문자로 읽는 전형적 혼동쌍 (실기기 제보 — "025"→"O25" 등).
 * 대조는 **카탈로그 번호를 정답지로** 하는 방향으로만 쓴다: 카탈로그가 숫자를
 * 기대하는 자리의 혼동 문자만 숫자로 인정하고, 프리픽스(TG/GG/SV…)의 진짜
 * 글자는 건드리지 않는다. 무차별 치환은 "TG30"→"7630" 식으로 프리픽스를 오염시킨다.
 */
const CONFUSABLE_TO_DIGIT: Record<string, string> = {
  O: "0",
  Q: "0",
  I: "1",
  L: "1",
  Z: "2",
  S: "5",
  G: "6",
  B: "8",
};

/** 혼동 문자를 숫자로 치환 (그 외 문자는 유지) */
function digitize(value: string): string {
  return value.replace(/[A-Z]/g, (ch) => CONFUSABLE_TO_DIGIT[ch] ?? ch);
}

/** 두 문자가 같은가 — 동일 또는 혼동쌍(어느 방향이든: 숫자→문자 오독, 문자→숫자 오독) */
function charLike(a: string, b: string): boolean {
  return a === b || CONFUSABLE_TO_DIGIT[a] === b || CONFUSABLE_TO_DIGIT[b] === a;
}

/**
 * 번호 파트 fuzzy 대조 — 카탈로그 파트("TG12", "025")를 정답지로 OCR 파트를 검사한다.
 * 프리픽스는 카탈로그의 글자 수만큼 혼동쌍 허용 문자 대조, 숫자부는 혼동 문자를
 * 숫자로 치환한 뒤 수치 비교(선행 0 무시). 카탈로그가 프리픽스+숫자 꼴이 아니면 불가.
 */
function partLike(ocrPart: string, catPart: string): boolean {
  const cat = catPart.match(/^([A-Z]*)(\d+)$/);
  if (!cat) return false;
  const [, catPrefix, catDigits] = cat;
  const ocrPrefix = ocrPart.slice(0, catPrefix.length);
  const ocrRest = ocrPart.slice(catPrefix.length);
  if (ocrPrefix.length !== catPrefix.length) return false;
  for (let i = 0; i < catPrefix.length; i++) {
    if (!charLike(ocrPrefix[i], catPrefix[i])) return false;
  }
  const digits = digitize(ocrRest);
  if (!/^\d+$/.test(digits)) return false;
  return Number(digits) === Number(catDigits);
}

/**
 * 컬렉터 번호 토큰 추출 패턴. 숫자 자리는 혼동 문자도 허용한다("O25/I65" 도 토큰) —
 * 단 전체에 실제 숫자가 하나는 있어야 해서("가스/오일" 같은 순수 단어 배제) 잡음이
 * 번호로 승격되지 않는다. 판정은 어차피 카탈로그 번호와의 대조가 한다.
 */
const NUMBER_TOKEN = /([A-Z]{0,4}[0-9OQILZSGB]{1,3})\s*\/\s*([A-Z]{0,4}[0-9OQILZSGB]{1,3})/gi;

/**
 * OCR 라인에서 컬렉터 번호 후보를 뽑는다. "a/b" 꼴(선택적 영문 프리픽스 — TG/GG/SV 등)
 * 이 1차 신호이고, 분자 단독도 함께 담는다(카탈로그 번호가 분자만인 세트 대비).
 */
export function extractCardNumbers(lines: string[]): Set<string> {
  const found = new Set<string>();
  for (const line of lines) {
    for (const match of line.matchAll(NUMBER_TOKEN)) {
      if (!/\d/.test(match[0])) continue;
      found.add(canonical(`${match[1]}/${match[2]}`));
      found.add(canonical(match[1]));
    }
  }
  return found;
}

/**
 * 카탈로그 직접 질의용 번호 **원문형** — canonical 과 달리 선행 0 을 유지한다.
 * BE 검색의 keyword 는 `CARD_NO = ?` 정확일치라 인쇄된 형태 그대로 던져야 맞는다
 * (카탈로그 CARD_NO 도 인쇄 표기를 저장한다). 공백만 제거, 대문자 통일.
 * 혼동 문자 치환형이 순수 숫자꼴("O25/I65"→"025/165")이 되면 그것도 질의에 더한다 —
 * 프리픽스 치환 오염형("TG30"→"7630")은 존재하지 않는 번호라 빈 결과로 무해하다.
 */
export function extractRawCardNumbers(lines: string[]): string[] {
  const found = new Set<string>();
  for (const line of lines) {
    for (const match of line.matchAll(NUMBER_TOKEN)) {
      if (!/\d/.test(match[0])) continue;
      const token = `${match[1]}/${match[2]}`.toUpperCase();
      found.add(token);
      const digitized = digitize(token);
      if (digitized !== token && /^\d+\/\d+$/.test(digitized)) found.add(digitized);
    }
  }
  return [...found];
}

/**
 * 이름 대조용 정규화 — NFKC(전각→반각, ＥＸ→EX) 후 소문자화, 글자·숫자만 남긴다.
 * 공백·중점(・)·기호를 걷어내 "Pikachu ex"/"피카츄ex" 표기 차이를 흡수한다.
 */
function normalizeNameText(value: string): string {
  return value.normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
}

/**
 * 카탈로그 카드명이 OCR 텍스트에 나타나는가 — 정규화 후 부분 문자열 대조.
 * 라인을 이어붙여 줄바꿈으로 갈린 이름도 잡는다. OCR 이 이름 글자 하나를 오독하면
 * 놓치지만(fail-safe — 부스트만 없을 뿐), 오탐보다 낫다. 1글자 이름은 우연 일치가
 * 잦아 배제한다.
 */
export function nameMatches(
  cardName: string | null | undefined,
  ocrLines: string[],
): boolean {
  if (!cardName || ocrLines.length === 0) return false;
  const name = normalizeNameText(cardName);
  if (name.length < 2) return false;
  return normalizeNameText(ocrLines.join("")).includes(name);
}

/**
 * 언어 게이트용 이름 증거 — nameMatches(전체 연속 일치)보다 한 단계 관대하게 OCR
 * 오독을 흡수한다: 정규화 후 텍스트 안 어느 창(window)과의 편집거리가 이름 4~7자는
 * 1, 8자 이상은 2 이하면 인정(4자 미만은 전체 일치만). 나머지 글자가 연속으로
 * 맞아야 해서 ko 리코그나이저의 환각 한글(2026-08-25 실기기)이 우연히 넘기는
 * 어렵고, 홀로 서체의 한 글자 오독으로 정상 카드가 거부되는 것은 막는다.
 */
/**
 * 언어 공통 접미 — 어떤 언어판에도 똑같이 라틴으로 인쇄돼("리자몽ex"/"リザードンex")
 * 언어 판별력이 0 인데, 이름 대조에서 확실한 글자를 공짜로 벌어준다. 2026-08-25
 * 실기기: ja 실물의 가나가 시각 유사 한글로 환각되고(リ→리) 접미 "ex"+오독 1자
 * 허용이 본체 오독을 가려 "리자돈ex"→"리자몽ex" 가 통과했다. 대조는 접미를 뗀
 * **본체**로만 한다.
 */
const UNIVERSAL_NAME_SUFFIX = /(?:vmax|vstar|ex|gx|v)$/;

export function nameEvidenceMatches(
  cardName: string | null | undefined,
  ocrLines: string[],
): boolean {
  if (!cardName || ocrLines.length === 0) return false;
  const name = normalizeNameText(cardName);
  const core = name.replace(UNIVERSAL_NAME_SUFFIX, "");
  // 접미를 떼면 본체가 사라지는 이름(사실상 없음)은 원형으로 폴백
  const target = core.length >= 2 ? core : name;
  const text = normalizeNameText(ocrLines.join(""));
  if (text.length === 0 || target.length < 2) return false;
  // 짧은 본체(2~3자)는 오독 허용 없이 전체 연속 일치만 — 허용을 주면 우연 일치가
  // 본체 길이를 압도한다
  if (target.length < 4) return text.includes(target);
  const maxErrors = target.length >= 8 ? 2 : 1;
  if (fuzzySubstringDistance(target, text) <= maxErrors) return true;
  // 가림 허용 (2026-08-25 사용자 요청 — 슬리브 모서리·손가락이 이름 일부를 덮으면
  // 전체 대조가 실패한다): 본체의 **연속 일치 구간**이 절반 이상(최소 3자)이면
  // 인정. 환각은 시각 유사 오독이라 정확한 연속 3자+ 를 만들기 어렵다.
  const minRun = Math.max(3, Math.ceil(target.length / 2));
  return longestCommonRun(target, text) >= minRun;
}

/** 두 문자열의 가장 긴 **연속** 공통 부분 문자열 길이 (LCSubstring DP) */
function longestCommonRun(a: string, b: string): number {
  let best = 0;
  let prev: number[] = new Array(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    const cur: number[] = new Array(b.length + 1).fill(0);
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        cur[j] = prev[j - 1] + 1;
        if (cur[j] > best) best = cur[j];
      }
    }
    prev = cur;
  }
  return best;
}

/** needle 과 haystack 의 모든 부분 문자열 중 최소 편집거리 (시작·끝 위치 자유) */
function fuzzySubstringDistance(needle: string, haystack: string): number {
  let prev: number[] = new Array(haystack.length + 1).fill(0);
  for (let i = 1; i <= needle.length; i++) {
    const cur: number[] = new Array(haystack.length + 1);
    cur[0] = i;
    for (let j = 1; j <= haystack.length; j++) {
      const cost = needle[i - 1] === haystack[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j - 1] + cost, prev[j] + 1, cur[j - 1] + 1);
    }
    prev = cur;
  }
  return Math.min(...prev);
}

/**
 * 이름 검증이 가능한 이름인가 — 정규화 후 2자 미만(뮤 등)은 우연 일치가 잦아
 * 검증 축으로 못 쓴다. 게이트는 이 경우 번호·세트 코드 증거로 대체한다
 * (2026-08-25 실사고 — 전면 이름 게이트가 "뮤"를 영영 거부).
 */
export function nameEvidenceVerifiable(cardName: string | null | undefined): boolean {
  return !!cardName && normalizeNameText(cardName).length >= 2;
}

/**
 * 세트 코드(cardsetCode)가 OCR 텍스트에 토큰으로 나타나는가 — **재판 판별용**
 * (같은 그림·이름·언어에 세트만 다른 인쇄판). 카드번호는 저대비 인쇄라 원거리에서
 * 안 읽히는데 세트 코드는 밝게 인쇄돼 먼저 읽히는 실사례(2026-08-25 뮤 "s8a" —
 * 번호 002/028 은 그림자+저대비로 판독 불가, s8a 는 선명). 오탐 방지: 숫자 포함
 * 2자 이상 또는 3자 이상 코드만, 정확 토큰 일치.
 */
export function setCodeMatches(
  cardsetCode: string | null | undefined,
  ocrLines: string[],
): boolean {
  if (!cardsetCode || ocrLines.length === 0) return false;
  const code = cardsetCode.trim().toUpperCase();
  if (!(code.length >= 3 || (code.length >= 2 && /\d/.test(code)))) return false;
  // 정확 토큰 일치 + 혼동쌍 허용("s8a"→"58a" 오독, 실기기 저대비 인쇄) — 길이는
  // 같아야 하고 글자별로 charLike 만 허용해 우연 일치를 좁게 유지한다.
  return ocrLines
    .join(" ")
    .toUpperCase()
    .split(/[^0-9A-Z]+/)
    .some(
      (token) =>
        token.length === code.length &&
        [...token].every((ch, i) => charLike(ch, code[i])),
    );
}

/**
 * 기술명이 OCR 텍스트에 나타나는가 — 접전 재랭킹 보조축(**부스트 전용, 게이트 아님**
 * — 2026-08-25 이름 게이트 철회의 교훈: 부재를 벌점으로 쓰지 않는다). 카드 본문의
 * 기술명은 카탈로그(`attackNames`)에 언어별로 있고 OCR 도 본문을 이미 읽는다 —
 * HP·속성과 달리 데이터가 이미 있는 "다른 포켓몬 접전" 판별축. 3자 미만(물기 등
 * 흔한 짧은 이름)은 우연 일치가 잦아 배제, 대조는 이름과 같은 오독·가림 허용.
 */
export function attackNameMatches(
  attackNames: string[] | null | undefined,
  ocrLines: string[],
): boolean {
  if (!attackNames?.length || ocrLines.length === 0) return false;
  return attackNames.some(
    (n) => normalizeNameText(n).length >= 3 && nameEvidenceMatches(n, ocrLines),
  );
}

/**
 * 카탈로그 CARD_NO 가 OCR 번호와 일치하는가.
 * 1차는 canonical 정확일치, 2차는 **원문 토큰 fuzzy**(혼동 문자 허용 — rawTokens 전달 시).
 * ⚠ 전체형 카드 번호를 분자만으로 인정하면 안 된다 — 같은 분자의 다른 세트
 * ("25/102" vs "25/165")가 전부 "일치"가 돼 교차 검증이 무력화된다 (분모가 가르는 축).
 */
export function numberMatches(
  cardNumber: string | null | undefined,
  ocrNumbers: Set<string>,
  rawTokens: string[] = [],
): boolean {
  if (!cardNumber) return false;
  const catNumber = catalogCardNumber(cardNumber);
  if (catNumber === "") return false;
  if (ocrNumbers.size > 0 && ocrNumbers.has(canonical(catNumber))) return true;
  const cat = catNumber.toUpperCase();
  const catParts = cat.split("/");
  for (const token of rawTokens) {
    const ocrParts = token.split("/");
    if (catParts.length === ocrParts.length) {
      if (catParts.every((p, i) => partLike(ocrParts[i], p))) return true;
    } else if (catParts.length === 1 && ocrParts.length === 2) {
      // 카탈로그 번호가 분자 단독인 세트 — 토큰의 분자와만 대조
      if (partLike(ocrParts[0], catParts[0])) return true;
    }
  }
  return false;
}

export function crossCheckMatches<T extends CrossCheckEntry>(
  matches: T[],
  cards: ReadonlyMap<number, CrossCheckCard>,
  ocrNumbers: Set<string>,
  rawTokens: string[] = [],
  ocrLines: string[] = [],
): CrossCheckResult<T> {
  const confirmedSerlnos = new Set<number>();
  const nameConfirmedSerlnos = new Set<number>();
  const setConfirmedSerlnos = new Set<number>();
  const attackConfirmedSerlnos = new Set<number>();
  for (const m of matches) {
    const card = cards.get(m.cardSerlno);
    if (numberMatches(card?.number, ocrNumbers, rawTokens)) {
      confirmedSerlnos.add(m.cardSerlno);
    }
    if (nameMatches(card?.name, ocrLines)) {
      nameConfirmedSerlnos.add(m.cardSerlno);
    }
    if (setCodeMatches(card?.cardsetCode, ocrLines)) {
      setConfirmedSerlnos.add(m.cardSerlno);
    }
    if (attackNameMatches(card?.attackNames, ocrLines)) {
      attackConfirmedSerlnos.add(m.cardSerlno);
    }
  }
  const noReorder = (): CrossCheckResult<T> => ({
    matches,
    confirmedSerlnos,
    nameConfirmedSerlnos,
    setConfirmedSerlnos,
    reordered: false,
  });
  if (matches.length === 0) return noReorder();
  if (confirmedSerlnos.size === 0) {
    // 번호가 안 읽힌 접전의 폴백 사다리 — 세트 코드(재판 판별) > 기술명(다른
    // 포켓몬 판별). 둘 다 **부스트 전용**(유일 일치 + 접전 격차 + top-1 미해당일
    // 때만 승격)이라 안 읽힌 카드를 걸러내는 일은 없다.
    const promoteUnique = (confirmed: Set<number>): CrossCheckResult<T> | null => {
      if (confirmed.size !== 1 || confirmed.has(matches[0].cardSerlno)) return null;
      const idx = matches.findIndex(
        (m) =>
          confirmed.has(m.cardSerlno) &&
          matches[0].similarity - m.similarity <= CROSS_CHECK_MAX_SIM_GAP,
      );
      if (idx <= 0) return null;
      return {
        matches: [matches[idx], ...matches.filter((_, i) => i !== idx)],
        confirmedSerlnos,
        nameConfirmedSerlnos,
        setConfirmedSerlnos,
        reordered: true,
      };
    };
    return promoteUnique(setConfirmedSerlnos) ?? promoteUnique(attackConfirmedSerlnos) ?? noReorder();
  }
  const both = (serlno: number) =>
    confirmedSerlnos.has(serlno) && nameConfirmedSerlnos.has(serlno);
  // 이름+번호 동시 일치 — 독립 신호 2개가 겹쳐 우연일 확률이 낮으므로 유사도 격차
  // 제한 없이 승격한다 (같은 번호의 언어판 차이를 이름 스크립트가 가르는 경우 포함).
  // 단 top-1 도 동시 일치면 임베딩 순서를 존중한다 (신호가 못 가른 것).
  let promoted = -1;
  if (!both(matches[0].cardSerlno)) {
    promoted = matches.findIndex((m) => both(m.cardSerlno));
  }
  if (promoted <= 0) {
    // 번호 단독 — 종전 규칙 그대로 (접전에서만, 격차 상한 CROSS_CHECK_MAX_SIM_GAP)
    if (confirmedSerlnos.has(matches[0].cardSerlno)) return noReorder();
    promoted = matches.findIndex(
      (m) =>
        confirmedSerlnos.has(m.cardSerlno) &&
        matches[0].similarity - m.similarity <= CROSS_CHECK_MAX_SIM_GAP,
    );
  }
  if (promoted <= 0) return noReorder();
  const reorderedList = [
    matches[promoted],
    ...matches.filter((_, i) => i !== promoted),
  ];
  return {
    matches: reorderedList,
    confirmedSerlnos,
    nameConfirmedSerlnos,
    setConfirmedSerlnos,
    reordered: true,
  };
}

/** 사이드카(card_meta.bin) 한 행 — 인덱스에 실린 카드 전량이 이 형태로 온다. */
export interface IndexCatalogRow {
  cardSerlno: number;
  name: string;
  number: string | null;
}

/** 엔진 `matcherCatalog` 의 packed 문자열 파싱 — 구분자는 0x1F(필드)·0x1E(레코드). */
export function parseIndexCatalog(packed: string): IndexCatalogRow[] {
  if (packed === "") return [];
  const rows: IndexCatalogRow[] = [];
  for (const record of packed.split("\u001E")) {
    const [serlno, name, number] = record.split("\u001F");
    const id = Number(serlno);
    if (!Number.isFinite(id) || name === undefined) continue;
    rows.push({ cardSerlno: id, name, number: number ? number : null });
  }
  return rows;
}

/**
 * 카드번호 확장 대조 — OCR 번호와 일치하는 카드를 **인덱스 전량**에서 찾는다.
 * top-5 안에 정답 인쇄판이 없을 때(유사도 밀림)의 구제 경로다.
 *
 * ⚠ 대상은 인덱스에 실린 카드뿐이고 **서버 검색으로 폴백하지 않는다**(2026-08-25
 * 사용자 결정) — 인덱스에 없는 카드는 애초에 인식될 수 없으니 번호로만 튀어나오는
 * 것도 맞지 않다. 종전 BE `keyword` 경로가 안고 있던 제약(ko CARD_NO 에 붙는 레어도
 * 꼬리표 때문에 번호 정확일치가 안 맞던 문제, size 50 컷)도 함께 사라진다.
 *
 * **유일할 때만** 반환한다 — 번호 일치가 2장 이상이면 OCR 카드명(ocrLines)까지 함께
 * 일치하는 카드로 한 번 더 가르고(같은 번호의 언어판·이름 차이), 그래도 유일하지
 * 않으면 잘못 짚기보다 침묵한다.
 */
export function findCardByNumber(
  rows: readonly IndexCatalogRow[],
  ocrNumbers: Set<string>,
  rawTokens: string[] = [],
  ocrLines: string[] = [],
): IndexCatalogRow | null {
  if (rows.length === 0 || ocrNumbers.size === 0) return null;
  const found: IndexCatalogRow[] = [];
  for (const row of rows) {
    if (numberMatches(row.number, ocrNumbers, rawTokens)) {
      found.push(row);
      // 2장을 넘어서면 이름으로 가르는 것 말고는 할 일이 없다 — 전량 수집 불필요
      if (found.length > 8) break;
    }
  }
  if (found.length === 1) return found[0];
  if (found.length > 1) {
    const byName = found.filter((row) => nameMatches(row.name, ocrLines));
    if (byName.length === 1) return byName[0];
  }
  return null;
}
